import React, { useState, useEffect, useContext } from 'react';
import { 
    Box, 
    Tabs, 
    Tab, 
    Card, 
    CardContent, 
    CardActions,
    Typography, 
    Grid, 
    Chip,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemText,
    Divider,
    IconButton,
    Collapse,
    Menu,
    MenuItem,
    TextField,
    Autocomplete,
    CircularProgress,
    ListItemSecondaryAction,
    Alert,
    Tooltip
} from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { useSelector, useDispatch } from 'react-redux';
import StorageIcon from '@mui/icons-material/Storage';
import FolderIcon from '@mui/icons-material/Folder';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloudIcon from '@mui/icons-material/Cloud';
import ComputerIcon from '@mui/icons-material/Computer';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PreviewIcon from '@mui/icons-material/Preview';
import PeopleIcon from '@mui/icons-material/People';
import TEXT from './text';
import { AppGlobalContext } from '../../../App';
import * as DbActions from './Actions';
import { initTable, useYjsDbGroups, useYjsDatabases } from '../../../services/yjs';

const DbBrowser = () => {
    const language = useSelector(state => state.App_Settings.language);
    const userEmail = useSelector(state => state.UserMetaData.email);
    const userUid = useSelector(state => state.UserMetaData.uid);
    const AppGlobalContxt = useContext(AppGlobalContext);
    const dispatch = useDispatch();

    // Yjs custom hooks for real-time collaborative state & offline IndexedDB persistence
    const { dbGroups: yjsDbGroups } = useYjsDbGroups({ uid: userUid });
    const { databases: yjsDatabases } = useYjsDatabases({ uid: userUid });

    // Synchronize Yjs state changes into Redux when dbGroups or databases update in real-time
    useEffect(() => {
        if (yjsDbGroups && yjsDbGroups.length > 0) {
            dispatch({
                type: DbActions.ActionTypes.LOAD_DBGROUPS_SUCCESS,
                payload: yjsDbGroups
            });
        }
    }, [yjsDbGroups, dispatch]);

    useEffect(() => {
        if (yjsDatabases && yjsDatabases.length > 0) {
            dispatch({
                type: DbActions.ActionTypes.LOAD_DATABASES_SUCCESS,
                payload: yjsDatabases
            });
        }
    }, [yjsDatabases, dispatch]);
    
    // Redux state for database operations
    const dbGroups = useSelector(state => state.DbBrowser?.dbGroups || []);
    const databases = useSelector(state => state.DbBrowser?.databases || []);
    const serverDatabases = useSelector(state => state.DbBrowser?.serverDatabases || []);
    const serverDbGroups = useSelector(state => state.DbBrowser?.serverDbGroups || []);
    const userNames = useSelector(state => state.DbBrowser?.userNames || {});
    const pushingItem = useSelector(state => state.DbBrowser?.operations?.pushing || null);
    const downloadingItem = useSelector(state => state.DbBrowser?.operations?.downloading || null);
    const syncChecking = useSelector(state => state.DbBrowser?.loading?.syncCheck || false);
    
    // Local UI state (not database-related)
    const [tabValue, setTabValue] = useState(0);
    const [selectedItem, setSelectedItem] = useState(null);
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [expandedCards, setExpandedCards] = useState({});
    const [quickViewOpen, setQuickViewOpen] = useState(false);
    const [quickViewDatabase, setQuickViewDatabase] = useState(null);
    const [viewMenuAnchor, setViewMenuAnchor] = useState(null);
    const [selectedViewDatabase, setSelectedViewDatabase] = useState(null);
    
    // Permission management state
    const [permissionsOpen, setPermissionsOpen] = useState(false);
    const [permissionItem, setPermissionItem] = useState(null); // The item being managed
    const [permissionItemType, setPermissionItemType] = useState(null); // 'database' or 'dbgroup'
    const [userSearchQuery, setUserSearchQuery] = useState('');
    const [userSearchResults, setUserSearchResults] = useState([]);
    const [searchingUsers, setSearchingUsers] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedPermissionType, setSelectedPermissionType] = useState('');
    const [updatingPermission, setUpdatingPermission] = useState(false);

    // Yjs custom hooks are the primary source of truth for local DB state
    useEffect(() => {
        console.log('[DBBROWSER] Yjs hooks initialized for dbGroups and databases');
    }, [dispatch]);

    // Fetch accessible items from server when user logs in
    useEffect(() => {
        dispatch(DbActions.fetchAccessibleItems(userEmail));
    }, [userEmail, dispatch]);

    // Check sync status when user is logged in
    useEffect(() => {
        if (userEmail && (dbGroups.length > 0 || databases.length > 0) && !syncChecking) {
            dispatch(DbActions.checkSyncStatus(userEmail, dbGroups, databases));
        }
    }, [userEmail, dbGroups, databases, syncChecking, dispatch]);

    // Ensure all loaded databases accessible to the user have their Yjs session initialized in window.Yjs.tables
    useEffect(() => {
        if (typeof window === 'undefined') return;
        databases.forEach(db => {
            if (db && db.name) {
                initTable(db.name, {
                    dbId: db.id,
                    roomName: `db-${db.id}`,
                    uid: userUid
                });
            }
        });
    }, [databases, userUid]);



    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleViewDetails = (item, type) => {
        setSelectedItem({ ...item, itemType: type });
        setDetailDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDetailDialogOpen(false);
        setSelectedItem(null);
    };

    // Fetch user names when detail dialog opens with global item
    useEffect(() => {
        if (!selectedItem || selectedItem.storageType !== 'global' || !userEmail) {
            return;
        }

        // Collect all UIDs from access control fields
        const allUids = [];
        ['owners', 'admins', 'contributors', 'readers', 'supervised'].forEach(field => {
            if (selectedItem[field]) {
                const uids = selectedItem[field].split(',').map(uid => uid.trim()).filter(uid => uid);
                allUids.push(...uids);
            }
        });

        // Remove duplicates
        const uniqueUids = [...new Set(allUids)];

        if (uniqueUids.length > 0) {
            dispatch(DbActions.fetchUserNames(userEmail, uniqueUids));
        }
    }, [selectedItem, userEmail, dispatch]);

    const toggleCardExpansion = (id) => {
        setExpandedCards(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return 'N/A';
        const date = new Date(timestamp);
        return date.toLocaleString(language === 'ar' ? 'ar-EG' : 'en-US');
    };

    const handleDownloadDatabase = async (serverDatabase) => {
        const confirmDownload = window.confirm(
            `${TEXT.ConfirmDownload?.[language] || 'Download database from server?'}\n\n${serverDatabase.name}`
        );

        if (!confirmDownload) return;

        dispatch(DbActions.downloadDatabase(userEmail, serverDatabase, language));
    };

    const handleDownloadDbGroup = async (serverDbGroup) => {
        const confirmDownload = window.confirm(
            `${TEXT.ConfirmDownload?.[language] || 'Download DBGroup from server?'}\n\n${serverDbGroup.name}`
        );

        if (!confirmDownload) return;

        dispatch(DbActions.downloadDbGroup(userEmail, serverDbGroup, language));
    };

    const handleViewRecordsClick = (event, database) => {
        setViewMenuAnchor(event.currentTarget);
        setSelectedViewDatabase(database);
    };

    const handleCloseViewMenu = () => {
        setViewMenuAnchor(null);
    };

    const handleQuickView = () => {
        setQuickViewDatabase(selectedViewDatabase);
        if (typeof window !== 'undefined' && selectedViewDatabase?.name) {
            initTable(selectedViewDatabase.name, {
                dbId: selectedViewDatabase.id,
                roomName: `db-${selectedViewDatabase.id}`,
                uid: userUid
            });
        }
        setQuickViewOpen(true);
        handleCloseViewMenu();
    };

    const handleOpenInPage = () => {
        // Save database ID to localStorage for the records page
        localStorage.setItem('selectedDatabaseId', selectedViewDatabase.id);
        // Ensure table is activated in window.Yjs.tables
        if (typeof window !== 'undefined' && selectedViewDatabase?.name) {
            initTable(selectedViewDatabase.name, {
                dbId: selectedViewDatabase.id,
                roomName: `db-${selectedViewDatabase.id}`,
                uid: userUid
            });
        }
        // Navigate to records page
        AppGlobalContxt.Body.setPageName('DbRecords');
        handleCloseViewMenu();
    };

    // Get merged list of databases (local from Yjs + server not downloaded)
    const getMergedDatabases = () => {
        // Primary local databases from Yjs custom hook, fallback to redux databases
        const localDbs = (yjsDatabases && yjsDatabases.length > 0) ? yjsDatabases : databases;
        console.log('[DBBROWSER] getMergedDatabases called, local count:', localDbs.length, 'server count:', serverDatabases.length);
        
        if (localDbs.length === 0 && serverDatabases.length === 0) {
            return [];
        }
        
        if (serverDatabases.length === 0) {
            return localDbs;
        }
        
        const localDbKeys = new Set(
            localDbs.map(db => {
                const group = db.dbGroupName || db.group || '';
                const name = db.name || '';
                return `${group}:${name}`.toLowerCase();
            })
        );
        
        const serverNotDownloaded = serverDatabases.filter(db => {
            const serverGroup = db.dbGroupName || db.group || '';
            const serverName = db.name || '';
            const serverKey = `${serverGroup}:${serverName}`.toLowerCase();
            return !localDbKeys.has(serverKey);
        });
        
        return [...localDbs, ...serverNotDownloaded.map(db => ({ 
            ...db, 
            isServerOnly: true,
            recordsCount: db.recordsCount || (db.records?.length || 0)
        }))];
    };

    // Get merged list of DBGroups (local from Yjs + server not downloaded)
    const getMergedDbGroups = () => {
        const localGroups = (yjsDbGroups && yjsDbGroups.length > 0) ? yjsDbGroups : dbGroups;
        const localGroupNames = new Set(localGroups.map(g => g.name));
        const serverNotDownloaded = serverDbGroups.filter(g => !localGroupNames.has(g.name));
        return [...localGroups, ...serverNotDownloaded.map(g => ({ ...g, isServerOnly: true }))];
    };

    const handlePushDbGroup = async (group) => {
        const confirmPush = window.confirm(
            `${TEXT.ConfirmPushDbGroup?.[language] || 'Push DBGroup to server?'}\n\n${group.name}`
        );

        if (!confirmPush) return;

        dispatch(DbActions.pushDbGroup(userEmail, group, language));
    };

    const handlePushDatabase = async (database) => {
        const confirmPush = window.confirm(
            `${TEXT.ConfirmPushDatabase?.[language] || 'Push Database to server?'}\n\n${database.name}`
        );

        if (!confirmPush) return;

        dispatch(DbActions.pushDatabase(userEmail, database, language));
    };

    // Permission Management Functions
    const handleManagePermissions = (item, type) => {
        setPermissionItem(item);
        setPermissionItemType(type);
        setPermissionsOpen(true);
        setSelectedUser(null);
        setSelectedPermissionType('');
        setUserSearchQuery('');
    };

    const handleClosePermissions = () => {
        setPermissionsOpen(false);
        setPermissionItem(null);
        setPermissionItemType(null);
        setSelectedUser(null);
        setSelectedPermissionType('');
        setUserSearchQuery('');
    };

    const handleUserSearch = async (searchValue) => {
        setUserSearchQuery(searchValue);
        
        if (searchValue.length < 2) {
            setUserSearchResults([]);
            return;
        }

        setSearchingUsers(true);
        dispatch(DbActions.searchUsers(userEmail, searchValue, (results) => {
            setUserSearchResults(results);
            setSearchingUsers(false);
        }));
    };

    const handleAddPermission = async () => {
        if (!selectedUser || !selectedPermissionType || !permissionItem) {
            alert(TEXT.SelectUserAndPermission?.[language] || 'Please select a user and permission type');
            return;
        }

        setUpdatingPermission(true);

        try {
            if (permissionItemType === 'dbgroup') {
                await dispatch(DbActions.updateDbGroupPermissions(
                    userEmail,
                    permissionItem.id,
                    selectedUser.uid,
                    selectedPermissionType,
                    'add',
                    language
                ));
            } else {
                // Database permissions
                await dispatch(DbActions.updateDatabasePermissions(
                    userEmail,
                    permissionItem.id,
                    selectedUser.uid,
                    selectedPermissionType,
                    'add',
                    language
                ));
            }

            // Reset form
            setSelectedUser(null);
            setSelectedPermissionType('');
            setUserSearchQuery('');
            setUserSearchResults([]);
        } catch (error) {
            console.error('Error adding permission:', error);
        } finally {
            setUpdatingPermission(false);
        }
    };

    const handleRemovePermission = async (userId, permissionType) => {
        const confirmRemove = window.confirm(
            TEXT.ConfirmRemovePermission?.[language] || 'Remove this permission?'
        );

        if (!confirmRemove) return;

        setUpdatingPermission(true);

        try {
            if (permissionItemType === 'dbgroup') {
                await dispatch(DbActions.updateDbGroupPermissions(
                    userEmail,
                    permissionItem.id,
                    userId,
                    permissionType,
                    'remove',
                    language
                ));
            } else {
                // Database permissions
                await dispatch(DbActions.updateDatabasePermissions(
                    userEmail,
                    permissionItem.id,
                    userId,
                    permissionType,
                    'remove',
                    language
                ));
            }
        } catch (error) {
            console.error('Error removing permission:', error);
        } finally {
            setUpdatingPermission(false);
        }
    };

    const getUsersForPermissionType = (type) => {
        if (!permissionItem) return [];
        
        const field = permissionItem[type];
        if (!field) return [];
        
        const uids = field.split(',').map(uid => uid.trim()).filter(uid => uid);
        return uids.map(uid => ({
            uid,
            displayName: userNames[uid] || uid
        }));
    };

    const canManagePermissions = (item) => {
        if (!item || item.storageType !== 'global') return false;
        if (!userUid) return false;
        
        const owners = (item.owners || '').split(',').map(uid => uid.trim()).filter(uid => uid);
        const admins = (item.admins || '').split(',').map(uid => uid.trim()).filter(uid => uid);
        
        return owners.includes(userUid) || admins.includes(userUid);
    };

    const renderDbGroupCard = (group) => {
        const isExpanded = expandedCards[`group-${group.id}`];
        
        return (
            <Grid item xs={12} sm={6} md={4} key={group.id}>
                <Card 
                    elevation={3}
                    sx={{ 
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        '&:hover': { boxShadow: 6 }
                    }}
                >
                    <CardContent sx={{ flexGrow: 1 }}>
                        <Box display="flex" alignItems="center" mb={1}>
                            <FolderIcon color="primary" sx={{ mr: 1 }} />
                            <Typography variant="h6" component="div">
                                {group.name}
                            </Typography>
                        </Box>
                        
                        <Box display="flex" gap={1} mb={2} flexWrap="wrap">
                            <Chip 
                                label={`${group.totalFields || 0} ${TEXT.Fields?.[language] || 'fields'}`}
                                size="small"
                                color="primary"
                                variant="outlined"
                            />
                            <Chip 
                                label={`v${group.version}`}
                                size="small"
                                color="secondary"
                            />
                            <Chip 
                                icon={group.storageType === 'local' ? <ComputerIcon /> : <CloudIcon />}
                                label={group.storageType === 'local' ? TEXT.Local?.[language] : TEXT.Global?.[language]}
                                size="small"
                                color={group.storageType === 'local' ? 'default' : 'success'}
                            />
                            {group.syncStatus === 'outdated' && (
                                <Chip 
                                    label={`${TEXT.Outdated?.[language] || 'Outdated'} (v${group.serverVersion})`}
                                    size="small"
                                    color="warning"
                                />
                            )}
                            {group.syncStatus === 'synced' && (
                                <Chip 
                                    label={TEXT.Synced?.[language] || 'Synced'}
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                />
                            )}
                        </Box>
                        
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            {TEXT.Created?.[language] || 'Created'}: {formatDate(group.timestamp)}
                        </Typography>
                        
                        <Collapse in={isExpanded}>
                            <Divider sx={{ my: 1 }} />
                            <Typography variant="body2" color="text.secondary">
                                {TEXT.FieldsList?.[language] || 'Fields'}:
                            </Typography>
                            <List dense>
                                {group.fields?.slice(0, 5).map((field, idx) => (
                                    <ListItem key={idx} sx={{ py: 0 }}>
                                        <ListItemText 
                                            primary={`${field.name} (${field.type})`}
                                            primaryTypographyProps={{ variant: 'body2' }}
                                        />
                                    </ListItem>
                                ))}
                                {group.fields?.length > 5 && (
                                    <ListItem sx={{ py: 0 }}>
                                        <ListItemText 
                                            primary={`... ${group.fields.length - 5} more`}
                                            primaryTypographyProps={{ variant: 'body2', fontStyle: 'italic' }}
                                        />
                                    </ListItem>
                                )}
                            </List>
                        </Collapse>
                    </CardContent>
                    
                    <CardActions>
                        <Button 
                            size="small" 
                            startIcon={<VisibilityIcon />}
                            onClick={() => handleViewDetails(group, 'dbgroup')}
                        >
                            {TEXT.ViewDetails?.[language] || 'View Details'}
                        </Button>
                        {group.isServerOnly ? (
                            <Button 
                                size="small" 
                                startIcon={<CloudIcon />}
                                onClick={() => handleDownloadDbGroup(group)}
                                disabled={downloadingItem === `group-${group.name}`}
                                color="primary"
                            >
                                {downloadingItem === `group-${group.name}` 
                                    ? (TEXT.Downloading?.[language] || 'Downloading...') 
                                    : (TEXT.Download?.[language] || 'Download')}
                            </Button>
                        ) : (
                            <>
                                {group.storageType === 'local' && (
                                    <Button 
                                        size="small" 
                                        startIcon={<CloudUploadIcon />}
                                        onClick={() => handlePushDbGroup(group)}
                                        disabled={pushingItem === `group-${group.id}`}
                                        color="success"
                                    >
                                        {pushingItem === `group-${group.id}` 
                                            ? (TEXT.Pushing?.[language] || 'Pushing...') 
                                            : (TEXT.PushToServer?.[language] || 'Push')}
                                    </Button>
                                )}
                                {canManagePermissions(group) && (
                                    <Tooltip title={TEXT.ManagePermissions?.[language] || 'Manage Permissions'}>
                                        <IconButton 
                                            size="small"
                                            onClick={() => handleManagePermissions(group, 'dbgroup')}
                                            color="primary"
                                        >
                                            <PeopleIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </>
                        )}
                        <IconButton 
                            size="small"
                            onClick={() => toggleCardExpansion(`group-${group.id}`)}
                            sx={{ ml: 'auto' }}
                        >
                            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                    </CardActions>
                </Card>
            </Grid>
        );
    };

    const renderDatabaseCard = (database) => {
        const isExpanded = expandedCards[`db-${database.id}`];
        
        return (
            <Grid item xs={12} sm={6} md={4} key={database.id}>
                <Card 
                    elevation={3}
                    sx={{ 
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        '&:hover': { boxShadow: 6 }
                    }}
                >
                    <CardContent sx={{ flexGrow: 1 }}>
                        <Box display="flex" alignItems="center" mb={1}>
                            <StorageIcon color="success" sx={{ mr: 1 }} />
                            <Typography variant="h6" component="div">
                                {database.name}
                            </Typography>
                        </Box>
                        
                        <Box display="flex" gap={1} mb={2} flexWrap="wrap">
                            <Chip 
                                label={`${database.recordsCount || 0} ${TEXT.Records?.[language] || 'records'}`}
                                size="small"
                                color="primary"
                                variant="outlined"
                            />
                            <Chip 
                                label={`v${database.version}`}
                                size="small"
                                color="secondary"
                            />
                            <Chip 
                                icon={database.isServerOnly ? <CloudIcon /> : (database.storageType === 'local' ? <ComputerIcon /> : <CloudIcon />)}
                                label={database.isServerOnly ? TEXT.ServerOnly?.[language] || 'Server Only' : (database.storageType === 'local' ? TEXT.Local?.[language] : TEXT.Global?.[language])}
                                size="small"
                                color={database.isServerOnly ? 'info' : (database.storageType === 'local' ? 'default' : 'success')}
                            />
                            {!database.isServerOnly && database.syncStatus === 'outdated' && (
                                <Chip 
                                    label={`${TEXT.Outdated?.[language] || 'Outdated'} (v${database.serverVersion})`}
                                    size="small"
                                    color="warning"
                                />
                            )}
                            {!database.isServerOnly && database.syncStatus === 'synced' && (
                                <Chip 
                                    label={TEXT.Synced?.[language] || 'Synced'}
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                />
                            )}
                        </Box>
                        
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            {TEXT.BasedOn?.[language] || 'Based on'}: <strong>{database.dbGroupName}</strong>
                        </Typography>
                        
                        <Typography variant="body2" color="text.secondary">
                            {TEXT.Created?.[language] || 'Created'}: {formatDate(database.timestamp)}
                        </Typography>
                        
                        <Collapse in={isExpanded}>
                            <Divider sx={{ my: 1 }} />
                            <Typography variant="body2" color="text.secondary">
                                {TEXT.RecentRecords?.[language] || 'Recent Records'}: {database.recordsCount}
                            </Typography>
                        </Collapse>
                    </CardContent>
                    
                    <CardActions sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                        <Button 
                            size="small" 
                            startIcon={<VisibilityIcon />}
                            onClick={() => handleViewDetails(database, 'database')}
                        >
                            {TEXT.ViewDetails?.[language] || 'View Details'}
                        </Button>
                        {!database.isServerOnly && (
                            <Button 
                                size="small" 
                                startIcon={<StorageIcon />}
                                onClick={(e) => handleViewRecordsClick(e, database)}
                                color="primary"
                                variant="outlined"
                            >
                                {TEXT.ManageRecords?.[language] || 'Manage Records'}
                            </Button>
                        )}
                        {database.isServerOnly ? (
                            <Button 
                                size="small" 
                                startIcon={<CloudIcon />}
                                onClick={() => handleDownloadDatabase(database)}
                                disabled={downloadingItem === `db-${database.name}`}
                                color="primary"
                            >
                                {downloadingItem === `db-${database.name}` 
                                    ? (TEXT.Downloading?.[language] || 'Downloading...') 
                                    : (TEXT.Download?.[language] || 'Download')}
                            </Button>
                        ) : (
                            <>
                                {database.storageType === 'local' && (
                                    <Button 
                                        size="small" 
                                        startIcon={<CloudUploadIcon />}
                                        onClick={() => handlePushDatabase(database)}
                                        disabled={pushingItem === `db-${database.id}`}
                                        color="success"
                                    >
                                        {pushingItem === `db-${database.id}` 
                                            ? (TEXT.Pushing?.[language] || 'Pushing...') 
                                            : (TEXT.PushToServer?.[language] || 'Push')}
                                    </Button>
                                )}
                                {canManagePermissions(database) && (
                                    <Tooltip title={TEXT.ManagePermissions?.[language] || 'Manage Permissions'}>
                                        <IconButton 
                                            size="small"
                                            onClick={() => handleManagePermissions(database, 'database')}
                                            color="primary"
                                        >
                                            <PeopleIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </>
                        )}
                        <IconButton 
                            size="small"
                            onClick={() => toggleCardExpansion(`db-${database.id}`)}
                            sx={{ ml: 'auto' }}
                        >
                            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                    </CardActions>
                </Card>
            </Grid>
        );
    };

    const renderDetailDialog = () => {
        if (!selectedItem) return null;

        const isDbGroup = selectedItem.itemType === 'dbgroup';

        return (
            <Dialog 
                open={detailDialogOpen} 
                onClose={handleCloseDialog}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center">
                        {isDbGroup ? <FolderIcon sx={{ mr: 1 }} /> : <StorageIcon sx={{ mr: 1 }} />}
                        {selectedItem.name}
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                                {TEXT.Type?.[language] || 'Type'}:
                            </Typography>
                            <Typography variant="body1">
                                {isDbGroup ? TEXT.DBGroupTemplate?.[language] || 'DBGroup Template' : TEXT.Database?.[language] || 'Database'}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                                {TEXT.Version?.[language] || 'Version'}:
                            </Typography>
                            <Typography variant="body1">v{selectedItem.version}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                                {TEXT.Storage?.[language] || 'Storage'}:
                            </Typography>
                            <Typography variant="body1">
                                {selectedItem.storageType === 'local' ? TEXT.Local?.[language] : TEXT.Global?.[language]}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                                {TEXT.Created?.[language] || 'Created'}:
                            </Typography>
                            <Typography variant="body1">{formatDate(selectedItem.timestamp)}</Typography>
                        </Grid>
                        
                        {!isDbGroup && (
                            <>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        {TEXT.BasedOn?.[language] || 'Based on'}:
                                    </Typography>
                                    <Typography variant="body1">{selectedItem.dbGroupName}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        {TEXT.Records?.[language] || 'Records'}:
                                    </Typography>
                                    <Typography variant="body1">{selectedItem.recordsCount}</Typography>
                                </Grid>
                            </>
                        )}
                        
                        {/* Access Control Section - Show only for global items */}
                        {selectedItem.storageType === 'global' && (
                            <>
                                <Grid item xs={12}>
                                    <Divider sx={{ my: 1 }} />
                                    <Typography variant="h6" gutterBottom>
                                        {TEXT.AccessControl?.[language] || 'Access Control'}
                                    </Typography>
                                </Grid>
                                
                                {selectedItem.owners && (
                                    <Grid item xs={6}>
                                        <Typography variant="body2" color="text.secondary">
                                            {TEXT.Owners?.[language] || 'Owners'}:
                                        </Typography>
                                        <Typography variant="body1">
                                            {selectedItem.owners.split(',').map(uid => {
                                                const trimmedUid = uid.trim();
                                                if (!trimmedUid) return null;
                                                const userName = userNames[trimmedUid]?.name;
                                                return userName || `UID: ${trimmedUid}`;
                                            }).filter(Boolean).join(', ') || TEXT.None?.[language] || 'None'}
                                        </Typography>
                                    </Grid>
                                )}
                                
                                {selectedItem.admins && (
                                    <Grid item xs={6}>
                                        <Typography variant="body2" color="text.secondary">
                                            {TEXT.Admins?.[language] || 'Admins'}:
                                        </Typography>
                                        <Typography variant="body1">
                                            {selectedItem.admins.split(',').map(uid => {
                                                const trimmedUid = uid.trim();
                                                if (!trimmedUid) return null;
                                                const userName = userNames[trimmedUid]?.name;
                                                return userName || `UID: ${trimmedUid}`;
                                            }).filter(Boolean).join(', ') || TEXT.None?.[language] || 'None'}
                                        </Typography>
                                    </Grid>
                                )}
                                
                                {selectedItem.contributors && (
                                    <Grid item xs={6}>
                                        <Typography variant="body2" color="text.secondary">
                                            {TEXT.Contributors?.[language] || 'Contributors'}:
                                        </Typography>
                                        <Typography variant="body1">
                                            {selectedItem.contributors.split(',').map(uid => {
                                                const trimmedUid = uid.trim();
                                                if (!trimmedUid) return null;
                                                const userName = userNames[trimmedUid]?.name;
                                                return userName || `UID: ${trimmedUid}`;
                                            }).filter(Boolean).join(', ') || TEXT.None?.[language] || 'None'}
                                        </Typography>
                                    </Grid>
                                )}
                                
                                {selectedItem.readers && (
                                    <Grid item xs={6}>
                                        <Typography variant="body2" color="text.secondary">
                                            {TEXT.Readers?.[language] || 'Readers'}:
                                        </Typography>
                                        <Typography variant="body1">
                                            {selectedItem.readers.split(',').map(uid => {
                                                const trimmedUid = uid.trim();
                                                if (!trimmedUid) return null;
                                                const userName = userNames[trimmedUid]?.name;
                                                return userName || `UID: ${trimmedUid}`;
                                            }).filter(Boolean).join(', ') || TEXT.None?.[language] || 'None'}
                                        </Typography>
                                    </Grid>
                                )}
                                
                                {selectedItem.supervised && (
                                    <Grid item xs={6}>
                                        <Typography variant="body2" color="text.secondary">
                                            {TEXT.Supervised?.[language] || 'Supervised'}:
                                        </Typography>
                                        <Typography variant="body1">
                                            {selectedItem.supervised.split(',').map(uid => {
                                                const trimmedUid = uid.trim();
                                                if (!trimmedUid) return null;
                                                const userName = userNames[trimmedUid]?.name;
                                                return userName || `UID: ${trimmedUid}`;
                                            }).filter(Boolean).join(', ') || TEXT.None?.[language] || 'None'}
                                        </Typography>
                                    </Grid>
                                )}
                            </>
                        )}
                        
                        {isDbGroup && selectedItem.fields && (
                            <Grid item xs={12}>
                                <Divider sx={{ my: 1 }} />
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    {TEXT.FieldsList?.[language] || 'Fields List'}:
                                </Typography>
                                <List dense>
                                    {selectedItem.fields.map((field, idx) => (
                                        <ListItem key={idx}>
                                            <ListItemText 
                                                primary={field.name}
                                                secondary={`${field.type}${field.mandatory ? ' (Required)' : ''}`}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>
                        {TEXT.Close?.[language] || 'Close'}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    const DebugButtons = () => {
        if (window.devMode) {
            return (
                <Button
                    variant="outlined"
                    onClick={() => {
                        console.log('[DBBROWSER] Manual reload triggered');
                        console.log('[DBBROWSER] Current databases state:', databases);
                        console.log('[DBBROWSER] Current dbGroups state:', dbGroups);
                        dispatch(DbActions.loadDatabases());
                        dispatch(DbActions.loadDbGroups());
                    }}
                    sx={{ mb: 2 }}
                >
                    🔄 Debug: Reload from IndexedDB
                </Button>
            )
        }
    }

    return (
        <Box sx={{ width: '100%' }}>
            <Typography variant="h4" gutterBottom>
                {TEXT.DatabaseBrowser?.[language] || 'Database Browser'}
            </Typography>
            <DebugButtons />
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={tabValue} onChange={handleTabChange}>
                    <Tab 
                        label={`${TEXT.Databases?.[language] || 'Databases'} (${getMergedDatabases().length})`}
                        icon={<StorageIcon />}
                        iconPosition="start"
                    />
                    <Tab 
                        label={`${TEXT.DBGroups?.[language] || 'DBGroups'} (${getMergedDbGroups().length})`}
                        icon={<FolderIcon />}
                        iconPosition="start"
                    />
                </Tabs>
            </Box>

            {tabValue === 0 && (
                <Box>
                    {/* Debug info */}
                    {console.log('[DBBROWSER] Rendering databases tab, count:', getMergedDatabases().length)}
                    {getMergedDatabases().length === 0 ? (
                        <Typography variant="body1" color="text.secondary" textAlign="center" py={4}>
                            {TEXT.NoDatabasesFound?.[language] || 'No databases found. Create one to get started.'}
                            <br />
                            <Typography variant="caption" sx={{ mt: 2, display: 'block' }}>
                                Debug: Local={databases.length}, Server={serverDatabases.length}, UserEmail={userEmail ? 'Yes' : 'No'}
                            </Typography>
                        </Typography>
                    ) : (
                        <Grid container spacing={3}>
                            {getMergedDatabases().map((db, idx) => renderDatabaseCard({ ...db, id: db.id || `server-${idx}` }))}
                        </Grid>
                    )}
                </Box>
            )}

            {tabValue === 1 && (
                <Box>
                    {getMergedDbGroups().length === 0 ? (
                        <Typography variant="body1" color="text.secondary" textAlign="center" py={4}>
                            {TEXT.NoDBGroupsFound?.[language] || 'No DBGroup templates found. Create one to get started.'}
                        </Typography>
                    ) : (
                        <Grid container spacing={3}>
                            {getMergedDbGroups().map((group, idx) => renderDbGroupCard({ ...group, id: group.id || `server-${idx}` }))}
                        </Grid>
                    )}
                </Box>
            )}

            {renderDetailDialog()}
            
            {/* Menu for View Records options */}
            <Menu
                anchorEl={viewMenuAnchor}
                open={Boolean(viewMenuAnchor)}
                onClose={handleCloseViewMenu}
            >
                <MenuItem onClick={handleQuickView}>
                    <PreviewIcon sx={{ mr: 1 }} />
                    {TEXT.QuickView?.[language] || 'Quick View'}
                </MenuItem>
                <MenuItem onClick={handleOpenInPage}>
                    <OpenInNewIcon sx={{ mr: 1 }} />
                    {TEXT.OpenInPage?.[language] || 'Open in Page'}
                </MenuItem>
            </Menu>

            {/* Quick View Dialog */}
            <Dialog
                open={quickViewOpen}
                onClose={() => setQuickViewOpen(false)}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: { height: '80vh' }
                }}
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box display="flex" alignItems="center">
                            <StorageIcon sx={{ mr: 1 }} />
                            {quickViewDatabase?.name} - {TEXT.QuickView?.[language] || 'Quick View'}
                        </Box>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                        {TEXT.Records?.[language] || 'Records'}: {quickViewDatabase?.records?.length || 0}
                    </Typography>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0 }}>
                    {quickViewDatabase && quickViewDatabase.records?.length > 0 ? (
                        <DataGrid
                            rows={quickViewDatabase.records.map((record, index) => ({ id: index + 1, ...record }))}
                            columns={
                                dbGroups.find(g => g.name === quickViewDatabase.dbGroupName)?.fields?.map(field => ({
                                    field: field.name,
                                    headerName: field.name + (field.mandatory ? ' *' : ''),
                                    flex: 1,
                                    minWidth: 150,
                                    type: field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'string',
                                })) || []
                            }
                            initialState={{
                                pagination: {
                                    paginationModel: { pageSize: 25, page: 0 },
                                },
                            }}
                            pageSizeOptions={[10, 25, 50]}
                            disableRowSelectionOnClick
                            slots={{ toolbar: GridToolbar }}
                            slotProps={{
                                toolbar: {
                                    showQuickFilter: true,
                                    quickFilterProps: { debounceMs: 500 },
                                },
                            }}
                            sx={{
                                border: 0,
                                '& .MuiDataGrid-cell:focus': {
                                    outline: 'none',
                                },
                            }}
                        />
                    ) : (
                        <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                            <Typography variant="body1" color="text.secondary">
                                {TEXT.NoRecords?.[language] || 'No records yet.'}
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setQuickViewOpen(false)}>
                        {TEXT.Close?.[language] || 'Close'}
                    </Button>
                    <Button 
                        variant="contained" 
                        onClick={() => {
                            setQuickViewOpen(false);
                            handleOpenInPage();
                        }}
                        startIcon={<OpenInNewIcon />}
                    >
                        {TEXT.OpenInPage?.[language] || 'Open in Page'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Permission Management Dialog */}
            <Dialog
                open={permissionsOpen}
                onClose={handleClosePermissions}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center">
                        <PeopleIcon sx={{ mr: 1 }} />
                        {TEXT.ManagePermissions?.[language] || 'Manage Permissions'}: {permissionItem?.name}
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    {/* Add Permission Section */}
                    <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                        <Typography variant="h6" gutterBottom>
                            {TEXT.AddPermission?.[language] || 'Add Permission'}
                        </Typography>
                        
                        <Autocomplete
                            options={userSearchResults}
                            getOptionLabel={(option) => `${option.name} (${option.email})`}
                            value={selectedUser}
                            onChange={(event, newValue) => setSelectedUser(newValue)}
                            inputValue={userSearchQuery}
                            onInputChange={(event, newValue) => handleUserSearch(newValue)}
                            loading={searchingUsers}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={TEXT.SearchUser?.[language] || 'Search User'}
                                    margin="normal"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {searchingUsers ? <CircularProgress size={20} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                            sx={{ mb: 2 }}
                        />

                        <Autocomplete
                            options={['owners', 'admins', 'contributors', 'readers', 'supervised']}
                            getOptionLabel={(option) => TEXT[option.charAt(0).toUpperCase() + option.slice(1)]?.[language] || option}
                            value={selectedPermissionType}
                            onChange={(event, newValue) => setSelectedPermissionType(newValue)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={TEXT.PermissionType?.[language] || 'Permission Type'}
                                    margin="normal"
                                />
                            )}
                            sx={{ mb: 2 }}
                        />

                        <Button
                            variant="contained"
                            onClick={handleAddPermission}
                            disabled={!selectedUser || !selectedPermissionType || updatingPermission}
                            fullWidth
                        >
                            {updatingPermission ? (TEXT.Adding?.[language] || 'Adding...') : (TEXT.AddPermission?.[language] || 'Add Permission')}
                        </Button>
                    </Box>

                    {/* Current Permissions Section */}
                    <Typography variant="h6" gutterBottom>
                        {TEXT.CurrentPermissions?.[language] || 'Current Permissions'}
                    </Typography>

                    {['owners', 'admins', 'contributors', 'readers', 'supervised'].map(permType => {
                        const users = getUsersForPermissionType(permType);
                        if (users.length === 0) return null;

                        return (
                            <Box key={permType} sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" color="primary" gutterBottom>
                                    {TEXT[permType.charAt(0).toUpperCase() + permType.slice(1)]?.[language] || permType}
                                </Typography>
                                <List dense>
                                    {users.map(user => (
                                        <ListItem key={user.uid}>
                                            <ListItemText 
                                                primary={user.displayName}
                                                secondary={user.uid}
                                            />
                                            <ListItemSecondaryAction>
                                                <IconButton
                                                    edge="end"
                                                    onClick={() => handleRemovePermission(user.uid, permType)}
                                                    disabled={updatingPermission}
                                                    size="small"
                                                    color="error"
                                                >
                                                    ✕
                                                </IconButton>
                                            </ListItemSecondaryAction>
                                        </ListItem>
                                    ))}
                                </List>
                            </Box>
                        );
                    })}

                    {!['owners', 'admins', 'contributors', 'readers', 'supervised'].some(type => getUsersForPermissionType(type).length > 0) && (
                        <Alert severity="info">
                            {TEXT.NoPermissionsYet?.[language] || 'No permissions configured yet'}
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClosePermissions}>
                        {TEXT.Close?.[language] || 'Close'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default DbBrowser;
